import 'dayjs/locale/ko'
import relativeTime from 'dayjs/plugin/relativeTime'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isBetween from 'dayjs/plugin/isBetween'
import toObject from 'dayjs/plugin/toObject'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import calendar from 'dayjs/plugin/calendar'
import weekday from 'dayjs/plugin/weekday'

const dayOfWeeks = {
  MON: '월', TUE: '화', WED: '수', THU: '목', FRI: '금', SAT: '토', SUN: '일'
}

const dayOfNums = {
  MON: 1, TUE: 2, WED: 3, THU: 4, FRI: 5, SAT: 6, SUN: 0
}

const indexDays = [
  '일', '월', '화', '수', '목', '금', '토'
]

let dayjs = require('dayjs')
dayjs.locale('ko')
dayjs.extend(relativeTime)
dayjs.extend(isSameOrBefore)
dayjs.extend(isSameOrAfter)
dayjs.extend(isBetween)
dayjs.extend(toObject)
dayjs.extend(customParseFormat)
dayjs.extend(calendar)
dayjs.extend(weekday)

export default class DateUtil {
  // 요일(한글명) 배열 반환
  getIndexDays () {
    return indexDays
  }

  // 요일(인덱스) 배열 반환
  getDayOfNums () {
    return dayOfNums
  }

  // 요일 (영문 - 한글) 배열 반환
  getDayOfWeeks () {
    return dayOfWeeks
  }

  // 영문 요일을 한글 요일로 변환
  convertDayKor (engName) {
    return dayOfWeeks[engName]
  }

  // 한글 요일 구하기기
  getDayOfWeek (date) {
    return indexDays[dayjs(date).get('day')]
  }

  // 입력 연/월의 1일 요일 인덱스 반환
  getStartDayIndex (year, month) {
    return dayjs(new Date(year, month - 1, 1)).format('d')
  }

  getDaysInMonth (year, month) {
    return dayjs(new Date(year, month - 1, 1)).daysInMonth()
  }

  // Date -> format('YYYY-MM-DD HH:mm:ss')
  toDateTimeDetail (dateVal) {
    return dayjs(dateVal).format('YYYY-MM-DD HH:mm:ss')
  }

  // Date -> format('YYYY-MM-DD HH:mm')
  toDateTime (dateVal) {
    return dayjs(dateVal).format('YYYY-MM-DD HH:mm')
  }

  // Date -> format('YYYY-MM-DD')
  toDate (dateVal, format) {
    if (!format) format = 'YYYY-MM-DD'
    return dayjs(dateVal).format(format)
  }

  // Date -> format('HH:mm')
  toTime (dateVal, format) {
    if (!format) format = 'HH:mm'
    return dayjs(dateVal).format(format)
  }

  // Date -> format(customFormat)
  toFormatDate (dateVal, format) {
    return dayjs(dateVal).format(format)
  }

  // 오늘날짜 반환 format('YYYY-MM-DD')
  getToday (format) {
    if (!format) format = 'YYYY-MM-DD'
    return dayjs(new Date()).format(format)
  }

  // 오늘날짜 반환 format(customFormat)
  getTodayFormat (format) {
    return dayjs(new Date()).format(format)
  }

  // 오늘날짜 반환 Date
  getTodayDate () {
    return dayjs(new Date()).date()
  }

  // 현재 월 반환
  getTodayMonth () {
    return dayjs(new Date()).month() + 1
  }

  // 현재 연도 반환
  getTodayYear () {
    return dayjs(new Date()).year()
  }

  // 입력 Date 일 반환
  getDate (dateVal) {
    return dayjs(dateVal).date()
  }

  // 입력 Date 월 반환
  getMonth (dateVal) {
    return dayjs(dateVal).month()
  }

  // 입력 Date 연도 반환
  getYear (dateVal) {
    return dayjs(dateVal).year()
  }

  // 이번주 시작 일요일
  getWeekSunday (dateVal, format) {
    if (!format) format = 'YYYY-MM-DD'
    return dayjs(dateVal).weekday(0).format(format)
  }

  // 다음주 시작 일요일
  getNextWeekSunday (dateVal) {
    return dayjs(dateVal).weekday(7).format('YYYY-MM-DD')
  }

  // 이번주 토요일
  getWeekSaturday (dateVal, format) {
    if (!format) format = 'YYYY-MM-DD'
    return dayjs(dateVal).weekday(6).format(format)
  }

  // 다음주 토요일
  getNextWeekSaturday (dateVal) {
    return dayjs(dateVal).weekday(13).format('YYYY-MM-DD')
  }

  // today < {date}
  isBefore (dateVal) {
    return dayjs().isBefore(dayjs(dateVal))
  }

  // today <= {date}
  isSameBefore (dateVal) {
    return dayjs().isSameOrBefore(dayjs(dateVal))
  }

  // today > {date}
  isAfter (dateVal) {
    return dayjs().isAfter(dayjs(dateVal))
  }

  // today >= {date}
  isSameAfter (dateVal) {
    return dayjs().isSameOrAfter(dayjs(dateVal))
  }

  subtract (dateVal, value, unit, format) {
    if (dateVal == null) dateVal = new Date()
    if (!format) format = 'YYYY-MM-DD'
    return {
      fromDate: dayjs(dateVal).subtract(value, unit).format(format),
      toDate: dayjs(dateVal).format(format)
    }
  }

  subtractMonth (dateVal, value) {
    return dayjs(dateVal).subtract(value, 'month').format('YYYY-MM-DD')
  }

  subtractYear (dateVal, value) {
    return dayjs(dateVal).subtract(value, 'year').format('YYYY-MM-DD')
  }

  subtractWeek (dateVal, value) {
    return dayjs(dateVal).subtract(value, 'week').format('YYYY-MM-DD')
  }

  subtractMinute (dateVal, value, format) {
    return dayjs(dateVal).subtract(value, 'minute').format((format !== undefined ? format : 'HH:mm'))
  }

  /**
   * from <= target <= to
   * @param from 시작일
   * @param to 종료일
   * @param target 대상일
   * @returns {boolean}
   */
  isBetween (from, to, target) {
    return dayjs(from).isBetween(target, dayjs(to), 'year')
  }

  // 달력정보 조회
  getCalendar (dateVal) {
    return dayjs().calendar(dateVal)
  }

  // Date to Object
  toObj (dateVal) {
    return dayjs(dateVal).toObject()
  }

  // form date gep
  getGapDate (dateVal) {
    let date = dayjs(dateVal).from(dayjs())
    return date
  }

  add (dateVal, value, unit, format) {
    if (!dateVal) dateVal = new Date()
    if (!format) format = 'YYYY-MM-DD'
    return dayjs(dateVal).add(value, unit).format(format)
  }

  startOf (dateVal, unit, format) {
    if (!dateVal) dateVal = new Date()
    if (!format) format = 'YYYY-MM-DD'
    if (!unit) unit = 'day'
    return dayjs(dateVal).startOf(unit).format(format)
  }

  endOf (dateVal, unit, format) {
    if (!dateVal) dateVal = new Date()
    if (!format) format = 'YYYY-MM-DD'
    if (!unit) unit = 'day'
    return dayjs(dateVal).endOf(unit).format(format)
  }

  diff (dateVal1, dateVal2, unit) {
    return parseInt(dayjs(dateVal1).diff(dateVal2, unit)) + 1
  }

  diffNow (dateVal, value, unit) {
    if (!dateVal) dateVal = new Date()
    if (!value) value = new Date()
    if (!unit) unit = 's'
    return dayjs(dateVal).diff(value, unit)
  }
}
